<template>
  <div class="consumption-list-container">
    <h2>消费记录列表</h2>
    <el-card shadow="hover">
      <div class="card-header">
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="搜索用户名称或消费单号"
            style="width: 200px; margin-right: 10px;"
            clearable
          >
            <template #prefix>
              <el-icon><search /></el-icon>
            </template>
          </el-input>
          <el-input
            v-model="searchForm.phone"
            placeholder="搜索用户手机号"
            style="width: 180px; margin-right: 10px;"
            clearable
          >
            <template #prefix>
              <el-icon><phone /></el-icon>
            </template>
          </el-input>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 300px; margin-right: 10px;"
          />
          <el-button type="primary" @click="handleSearch">
            <el-icon><search /></el-icon>
            搜索
          </el-button>
          
        </div>
      </div>
      
      <el-table :data="consumptions" border stripe style="width: 100%" v-loading="loading">
        <el-table-column prop="user.name" label="用户名称" />
        <el-table-column prop="order_no" label="消费单号" />
        <el-table-column prop="total_amount" label="总金额" />
        <el-table-column prop="created_at" label="消费时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.status === 'completed' ? 'success' : 'info'">
              {{ getStatusName(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="类型" width="100">
          <template #default="scope">
            {{ getTypeName(scope.row.type) }}
          </template>
        </el-table-column>
        <el-table-column prop="remarks" label="描述" />
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleViewDetails(scope.row)">
              <el-icon><view /></el-icon> 查看详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 消费详情抽屉 -->
    <el-drawer
      v-model="detailDrawerVisible"
      :title="`消费详情 - ${consumptionDetail.order_no || ''}`"
      direction="rtl"
      size="600px"
    >
      <div class="consumption-detail" v-if="consumptionDetail.id">
        <el-card shadow="never" class="detail-card">
          <el-descriptions title="基本信息" :column="2">
            <el-descriptions-item label="用户ID">{{ consumptionDetail.user_id }}</el-descriptions-item>
            <el-descriptions-item label="用户名称">{{ consumptionDetail.user?.name || '未知' }}</el-descriptions-item>
            <el-descriptions-item label="消费单号">{{ consumptionDetail.order_no }}</el-descriptions-item>
            <el-descriptions-item label="消费类型">{{ getTypeName(consumptionDetail.type) }}</el-descriptions-item>
            <el-descriptions-item label="状态">{{ getStatusName(consumptionDetail.status) }}</el-descriptions-item>
            <el-descriptions-item label="消费时间">{{ formatDate(consumptionDetail.created_at) }}</el-descriptions-item>
            <el-descriptions-item label="总金额">¥{{ consumptionDetail.total_amount }}</el-descriptions-item>
            <el-descriptions-item label="描述" :span="2">{{ consumptionDetail.remarks || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>
        
        <el-card shadow="never" class="detail-card mt-20">
          <template #header>
            <h3 class="detail-section-title m-0">消费项目列表</h3>
          </template>
          <el-table :data="consumptionDetail.items" border style="width: 100%" size="small">
            <el-table-column prop="name" label="项目名称" />
            <el-table-column prop="amount" label="金额" />
            <el-table-column prop="consumed_at" label="消费时间" width="180">
              <template #default="scope">
                {{ formatDate(scope.row.consumed_at) }}
              </template>
            </el-table-column>
            <el-table-column prop="remarks" label="备注" />
            <el-table-column prop="created_at" label="添加时间" width="180">
              <template #default="scope">
                {{ formatDate(scope.row.created_at) }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>
    </el-drawer>
    

  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Search, Plus, Phone } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'ConsumptionList',
  components: {
    Search,
    Plus,
    Phone
  },
  setup() {
    const router = useRouter();
    
    const consumptions = ref([]);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    
    // 搜索表单
    const searchForm = reactive({
      keyword: '',
      phone: '',
      itemName: ''
    });
    
    // 日期范围
    const dateRange = ref([]);
    
    // 详情抽屉
    const detailDrawerVisible = ref(false);
    const consumptionDetail = ref({
      id: '',
      user_id: '',
      user: null,
      consumption_no: '',
      type: '',
      status: '',
      consumption_time: '',
      total_amount: 0,
      description: '',
      items: []
    });
    

    
    // 获取消费记录列表
    const fetchConsumptions = async () => {
      loading.value = true;
      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
          search: searchForm.keyword,
          phone: searchForm.phone,
          item_name: searchForm.itemName
        };
        
        // 添加日期范围筛选
        if (dateRange.value && dateRange.value.length === 2) {
          params.start_date = dateRange.value[0];
          params.end_date = dateRange.value[1];
        }
        
        const response = await request({
          url: '/manage/consumptions',
          method: 'get',
          params
        });
        consumptions.value = response.data;
        total.value = response.total;
      } catch (error) {
        ElMessage.error('获取消费记录失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };
    
    // 获取消费记录详情
    const fetchConsumptionDetail = async (id) => {
      try {
        const response = await request({
          url: `/manage/consumptions/${id}`,
          method: 'get'
        });
        consumptionDetail.value = response.data;
      } catch (error) {
        ElMessage.error('获取消费记录详情失败');
        console.error(error);
      }
    };
    
    // 搜索消费记录
    const handleSearch = () => {
      currentPage.value = 1;
      fetchConsumptions();
    };
    
    // 页面加载时获取消费记录
    onMounted(() => {
      fetchConsumptions();
    });
    
    // 跳转到添加消费记录页面
    const handleCreate = () => {
      router.push('/consumptions/create');
    };
    
    // 查看消费详情
    const handleViewDetails = (consumption) => {
      fetchConsumptionDetail(consumption.id);
      detailDrawerVisible.value = true;
    };
    

    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchConsumptions();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchConsumptions();
    };
    
    // 获取消费类型名称
    const getTypeName = (type) => {
      const typeMap = {
        'room_fee': '房费',
        'system_consumption': '系统消费',
        'luky_cloud': '路客云',
        'normal': '普通消费',
        'room': '房间消费',
        'food': '餐饮消费',
        'entertainment': '娱乐消费',
        'other': '其他消费'
      };
      return typeMap[type] || type;
    };
    
    // 获取状态名称
    const getStatusName = (status) => {
      const statusMap = {
        'completed': '已完成',
        'pending': '未完成'
      };
      return statusMap[status] || status;
    };
    
    // 格式化日期
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };
    

    
    return {
      consumptions,
      currentPage,
      pageSize,
      total,
      loading,
      searchForm,
      dateRange,
      detailDrawerVisible,
      consumptionDetail,
      handleCreate,
      handleViewDetails,
      handleSearch,
      handleSizeChange,
      handleCurrentChange,
      getTypeName,
      getStatusName,
      formatDate
    };
  }
};
</script>

<style scoped>
.consumption-list-container {
  padding: 20px;
}

.consumption-list-container h2 {
  margin-bottom: 20px;
  color: #333;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 消费详情抽屉样式 */
.consumption-detail {
  padding: 0;
}

.detail-card {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.mt-20 {
  margin-top: 20px;
}

.detail-section-title {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.el-descriptions {
  margin-bottom: 0;
}

.el-descriptions__label {
  font-weight: bold;
  color: #606266;
}

.el-descriptions__value {
  color: #303133;
}

.el-drawer__header {
  border-bottom: 1px solid #ebeef5;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.el-drawer__title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.el-drawer__body {
  padding: 0 20px 20px;
}

.el-card__header {
  padding: 12px 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}


</style>